import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { Country, State } from "country-state-city";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useParams } from "react-router-dom";
import {
  useCreateSubscriptionPaymentMutation,
  useGetPaymentMethodByIdQuery,
  useUpdateSubscriptionPaymentMutation,
} from "../../../services";
import { useGetOrganisationByIdQuery } from "../../../services";
import { selectOrganisationId } from "../../../redux/organisationReducer";
import { PaymentSubmitRequest } from "../../../types/subscription/subscriptionPayment";

const PaymentDetails: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const isEdit = !!paymentId;

  const [createSubscriptionPaymentMutation] =
    useCreateSubscriptionPaymentMutation();
  const [updateSubscriptionPaymentMutation] =
    useUpdateSubscriptionPaymentMutation();

  const { data: paymentMethodData, isLoading: isLoadingPayment } =
    useGetPaymentMethodByIdQuery(paymentId as string, { skip: !isEdit });

  const organisationId = useAppSelector(selectOrganisationId);
  const { data: organization } = useGetOrganisationByIdQuery(organisationId as string, { skip: !organisationId });
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  const [form, setForm] = useState({
    organisation: organisationId,
    name: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    expiryDate: "", // For edit mode MM/YY or MM/YYYY
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sync organisationId to form when it becomes available in Redux
  useEffect(() => {
    if (organisationId) {
      setForm((prev) => ({ ...prev, organisation: organisationId }));
    }
  }, [organisationId]);

  // Populate form if in edit mode
  useEffect(() => {
    if (isEdit && paymentMethodData) {
      const pm = paymentMethodData.result || paymentMethodData.data; // Adjust based on API response structure
      if (pm) {
        setForm({
          organisation: pm.organisation || organisationId,
          name: pm.billingDetails?.name || "",
          address: pm.billingDetails?.address || pm.billingDetails?.line1 || "",
          city: pm.billingDetails?.city || "",
          state: pm.billingDetails?.state || "",
          country: pm.billingDetails?.country || "",
          postalCode: pm.billingDetails?.postal || pm.billingDetails?.postal_code || "",
          expiryDate: pm.card
            ? `${pm.card.expiryMonth}/${pm.card.expiryYear.toString().slice(-2)}`
            : "",
        });
        setCountrySearch(pm.billingDetails?.country || "");
        setSelectedCountryCode(pm.billingDetails?.country || "");
        setSelectedStateCode(pm.billingDetails?.state || "");
      }
    }
  }, [isEdit, paymentMethodData]);

  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const elementStyles = {
    style: {
      base: {
        fontSize: "14px",
        color: theme === "dark" ? "#f3f4f6" : "#111827",
        fontFamily: "Outfit, sans-serif",
        lineHeight: "24px",
        "::placeholder": {
          color: "#6b7280",
        },
      },
      invalid: {
        color: "#ef4444",
      },
    },
  };

  const inputClassBase =
    "w-full rounded-md px-3 h-[38px] outline-none transition-colors";
  const inputClass =
    theme === "dark"
      ? `${inputClassBase} bg-[#111111] border border-[#2C2C2C] placeholder-gray-500 focus:border-[#B6CB24]`
      : `${inputClassBase} bg-white border border-gray-300 placeholder-gray-500 focus:border-[#B6CB24]`;

  const labelClass = theme === "dark" ? "text-gray-100" : "text-gray-700";
  const dividerClass =
    theme === "dark" ? "border-[#2C2C2C]" : "border-gray-200";

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    let { name, value } = e.target;

    if (name === "expiryDate") {
      // Basic formatting for MM/YY
      value = value.replace(/\D/g, "");
      if (value.length > 2) {
        value = value.slice(0, 2) + "/" + value.slice(2, 4);
      }
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateField = (name: string, value: string) => {
    let error = "";

    switch (name) {
      case "name":
        if (!value.trim()) error = "Name is required";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setErrors((prev) => ({ ...prev, name: "Name is required" }));
      return;
    }

    setIsSubmitting(true);
    setStripeError(null);

    try {
      if (isEdit) {
        // Handle Edit
        const [month, year] = form.expiryDate.split("/");
        const payload = {
          billingDetails: {
            name: form.name,
            address: form.address,
            city: form.city,
            state: form.state,
            country: form.country,
            postal: form.postalCode,
          },
          card: {
            expiryMonth: month ? parseInt(month) : undefined,
            expiryYear: year ? parseInt("20" + year) : undefined,
          }
        };
        await updateSubscriptionPaymentMutation({ id: paymentId, payload }).unwrap();
        navigate(-1);
      } else {
        // Handle Create
        if (!stripe || !elements) {
          setIsSubmitting(false);
          return;
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
          setIsSubmitting(false);
          return;
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
          billing_details: {
            name: form.name || "",
            address: {
              line1: form.address,
              city: form.city,
              state: form.state,
              country: form.country,
              postal_code: form.postalCode,
            },
            email: organization?.subscriber?.email || "",
            phone: organization?.phone || "",
          },
        });

        if (error) {
          setStripeError(error.message || "An error occurred");
          setIsSubmitting(false);
          return;
        }

        const payload: PaymentSubmitRequest = {
          organisation: organisationId || form.organisation,
          paymentMethodId: paymentMethod.id,
        };

        await createSubscriptionPaymentMutation(payload).unwrap();
        navigate(-1);
      }
    } catch (err) {
      // Error handled by RTK Query's onQueryStarted
    } finally {
      setIsSubmitting(false);
    }
  };

  // sidebar width handling (64px collapsed, 256px expanded)
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // Country and State logic
  const allCountries = Country.getAllCountries();
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [selectedStateCode, setSelectedStateCode] = useState("");

  // Searchable Country Dropdown Logic
  const [countrySearch, setCountrySearch] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCountrySelect = (c: any) => {
    setCountrySearch(c.name);
    setSelectedCountryCode(c.isoCode);
    setSelectedStateCode("");
    setForm((prev) => ({
      ...prev,
      country: c.isoCode,
      state: "",
    }));
    setIsCountryDropdownOpen(false);
  };


  // Get states for selected country
  const stateOptions = selectedCountryCode
    ? State.getStatesOfCountry(selectedCountryCode)
    : [];



  // When state changes, update state and country accordingly
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateCode = e.target.value;
    setSelectedStateCode(stateCode);
    const stateObj = stateOptions.find((s) => s.isoCode === stateCode);
    setForm((prev) => ({
      ...prev,
      state: stateObj?.name || stateCode,
      country: stateObj?.countryCode || prev.country,
    }));
    if (stateObj?.countryCode) {
      setSelectedCountryCode(stateObj.countryCode);
    }
  };

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen  ${theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-4">
        <h1
          className={`text-xl font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-900"
            }`}
        >
          Subscription Overview
        </h1>
        <div className="text-sm mt-1 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Link
            to="/"
            className={theme === "dark" ? "text-gray-400" : "text-gray-500"}
          >
            Dashboard
          </Link>
          <span className={theme === "dark" ? "text-gray-600" : "text-gray-400"}>
            &gt;
          </span>
          <span className="text-[#B6CB24] font-medium">{isEdit ? "Edit Payment Method" : "Payment Details"}</span>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={onSubmit}
        className={`${cardClass} rounded-lg p-2 md:p-4`}
      >
        {/* Payment details */}
        <div className={`mt-2 mb-3 pb-1 border-b ${dividerClass}`}>
          <h2 className="text-sm font-semibold">{isEdit ? "Edit Payment Method" : "Payment details"}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={`block text-sm mb-1  ${labelClass}`}>Name</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter here"
              className={inputClass}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>
          <div className="relative">
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Card number
            </label>

            {isEdit ? (
              <input
                value={`•••• •••• •••• ${paymentMethodData?.result?.card?.last4 || "****"}`}
                disabled
                className={`${inputClass} opacity-50 cursor-not-allowed`}
              />
            ) : (
              <div className={`${inputClass} flex items-center`}>
                <div className="w-full">
                  <CardNumberElement options={elementStyles} />
                </div>
              </div>
            )}

            {/* SVG inside input */}
            <div className="absolute right-3 top-[34px]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="27"
                height="17"
                viewBox="0 0 27 17"
                fill="none"
              >
                <path
                  d="M17.1505 1.78348H9.84961V14.9034H17.1505V1.78348Z"
                  fill="#FF5F00"
                />
                <path
                  d="M10.3133 8.34388C10.3121 7.08034 10.5985 5.8331 11.1506 4.69658C11.7027 3.56005 12.5062 2.56403 13.5002 1.78393C12.2693 0.816414 10.791 0.214734 9.23432 0.0476536C7.67764 -0.119426 6.10536 0.154837 4.69718 0.839095C3.289 1.52335 2.10174 2.58999 1.27112 3.91711C0.440489 5.24422 0 6.77826 0 8.34388C0 9.90951 0.440489 11.4436 1.27112 12.7707C2.10174 14.0978 3.289 15.1644 4.69718 15.8487C6.10536 16.5329 7.67764 16.8072 9.23432 16.6401C10.791 16.473 12.2693 15.8714 13.5002 14.9038C12.5062 14.1237 11.7027 13.1277 11.1506 11.9912C10.5985 10.8547 10.3121 9.60742 10.3133 8.34388V8.34388Z"
                  fill="#EB001B"
                />
                <path
                  d="M26.9997 8.34388C26.9998 9.90948 26.5594 11.4435 25.7288 12.7706C24.8982 14.0977 23.711 15.1644 22.3028 15.8487C20.8947 16.5329 19.3224 16.8072 17.7658 16.6401C16.2091 16.473 14.7309 15.8714 13.5 14.9038C14.4931 14.123 15.2959 13.1268 15.848 11.9904C16.4 10.8541 16.6868 9.60722 16.6868 8.34388C16.6868 7.08054 16.4 5.83369 15.848 4.69734C15.2959 3.561 14.4931 2.56482 13.5 1.78392C14.7309 0.816411 16.2091 0.21473 17.7658 0.0476521C19.3224 -0.119426 20.8947 0.154842 22.3028 0.839107C23.711 1.52337 24.8982 2.59002 25.7288 3.91714C26.5594 5.24425 26.9998 6.77828 26.9997 8.34388V8.34388Z"
                  fill="#F79E1B"
                />
                <path
                  d="M26.2046 13.514V13.2454H26.3129V13.1907H26.0371V13.2454H26.1455V13.514H26.2046ZM26.7402 13.514V13.1901H26.6556L26.5584 13.4129L26.4611 13.1901H26.3765V13.514H26.4362V13.2697L26.5274 13.4803H26.5893L26.6805 13.2692V13.514H26.7402Z"
                  fill="#F79E1B"
                />
              </svg>
            </div>
          </div>

          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Expiry date
            </label>
            {isEdit ? (
              <input
                name="expiryDate"
                value={form.expiryDate}
                onChange={handleChange}
                placeholder="MM/YY"
                maxLength={5}
                className={inputClass}
              />
            ) : (
              <div className={`${inputClass} flex items-center`}>
                <div className="w-full">
                  <CardExpiryElement options={elementStyles} />
                </div>
              </div>
            )}
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Security code (CVC)
            </label>
            {isEdit ? (
              <input
                value="•••"
                disabled
                className={`${inputClass} opacity-50 cursor-not-allowed`}
              />
            ) : (
              <div className={`${inputClass} flex items-center`}>
                <div className="w-full">
                  <CardCvcElement options={elementStyles} />
                </div>
              </div>
            )}
          </div>

        </div>
        {stripeError && (
          <p className="text-red-500 text-xs mt-2">{stripeError}</p>
        )}


        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Billing address
            </label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Enter here"
              className={`${inputClass} h-20 resize-none`}
            />
          </div>

          {/* NEW: group these four in one row on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:col-span-2">
            <div ref={countryDropdownRef} className="relative">
              <label className={`block text-sm mb-1 ${labelClass}`}>
                Country
              </label>
              <input
                type="text"
                placeholder="Search country"
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setIsCountryDropdownOpen(true);
                  // Make sure to reset country code if user clears input or types something new without selecting
                  if (e.target.value === "") {
                    setSelectedCountryCode("");
                    setForm(prev => ({ ...prev, country: "" }));
                  }
                }}
                onFocus={() => setIsCountryDropdownOpen(true)}
                className={inputClass}
              />
              {isCountryDropdownOpen && (
                <div
                  className={`absolute z-10 w-full mt-1 max-h-60 overflow-auto rounded-md border shadow-lg ${theme === "dark"
                    ? "bg-[#111111] border-[#2C2C2C]"
                    : "bg-white border-gray-300"
                    }`}
                >
                  {allCountries
                    .filter((c) =>
                      c.name.toLowerCase().includes(countrySearch.toLowerCase())
                    )
                    .map((c) => (
                      <div
                        key={c.isoCode}
                        className={`px-3 py-2 cursor-pointer text-sm ${theme === "dark"
                          ? "text-gray-100 hover:bg-[#2C2C2C]"
                          : "text-gray-900 hover:bg-gray-100"
                          }`}
                        onClick={() => handleCountrySelect(c)}
                      >
                        {c.name} <span className="opacity-50 text-xs ml-1">({c.isoCode})</span>
                      </div>
                    ))}
                  {allCountries.filter((c) =>
                    c.name.toLowerCase().includes(countrySearch.toLowerCase())
                  ).length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No countries found
                      </div>
                    )}
                </div>
              )}
            </div>

            <div>
              <label className={`block text-sm mb-1 ${labelClass}`}>
                State
              </label>
              <select
                name="state"
                value={selectedStateCode}
                onChange={handleStateChange}
                className={inputClass}
              >
                <option value="" disabled>
                  Choose from list
                </option>
                {stateOptions.map((s) => (
                  <option key={s.isoCode} value={s.isoCode}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm mb-1 ${labelClass}`}>City</label>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="Enter here"
                className={inputClass}
              />
            </div>

            <div>
              <label className={`block text-sm mb-1 ${labelClass}`}>
                Postal / Zip code
              </label>
              <input
                name="postalCode"
                value={form.postalCode}
                onChange={handleChange}
                placeholder="Enter here"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div
          className={`mt-4 pt-3 flex items-center gap-3 border-t ${dividerClass}`}
        >
          <button
            type="submit"
            disabled={isSubmitting || (!isEdit && !stripe)}
            className={`px-6 py-1.5 font-medium text-black rounded-[10px] border bg-[radial-gradient(94.17%_100%_at_52.48%_0%,rgba(198,217,44,0.80)_0%,rgba(83,91,16,0.80)_100%)] ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            style={{ borderColor: "var(--menu-active-border, #C6D92C)" }}
          >
            {isSubmitting ? "Saving..." : (isEdit ? "Update" : "Save")}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-1.5 font-medium text-white rounded-[10px] border bg-[radial-gradient(94.17%_100%_at_52.48%_0%,rgba(46,46,46,0.80)_0%,rgba(71,71,71,0.80)_100%)]"
            style={{ borderColor: "var(--light-border-box, #9B9DB6)" }}
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentDetails;
